import { useQuery } from '@tanstack/react-query';
import { ChevronDown, FolderOpen, Sparkles } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { listRepos } from '../../api/repos.js';
import { useApi } from '../../api/use-api.js';
import { cn } from '../../lib/cn.js';

interface RepoSwitcherProps {
  readonly repo: string | null;
}

export function RepoSwitcher({ repo }: RepoSwitcherProps): JSX.Element | null {
  const api = useApi();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  const { data } = useQuery({
    queryKey: ['repos-switcher'],
    queryFn: () => listRepos(api),
    refetchInterval: 60_000,
  });

  useEffect(() => {
    const onClick = (e: MouseEvent): void => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  if (!repo) return null;
  const repos = data ?? [];
  const active = repos.find((r) => r.slug === repo);
  const label = active?.displayName ?? repo;

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={cn(
          'flex cursor-pointer items-center gap-2 rounded-lg border px-3 py-1.5 text-sm transition-all',
          open
            ? 'border-accent/40 bg-accent/10 text-text-primary'
            : 'border-border-subtle bg-surface text-text-secondary hover:border-border-default hover:bg-hover hover:text-text-primary',
        )}
      >
        <span className="h-1.5 w-1.5 animate-pulse rounded-full bg-node-function" />
        <span className="max-w-[180px] truncate font-medium">{label}</span>
        <ChevronDown
          className={cn(
            'h-3 w-3 text-text-muted transition-transform duration-200',
            open && 'rotate-180',
          )}
        />
      </button>

      {open && (
        <div className="absolute left-0 top-full z-50 mt-1.5 w-72 animate-slide-up overflow-hidden rounded-xl border border-border-subtle bg-surface shadow-panel">
          {repos.length > 0 && (
            <div>
              <div className="px-3 pb-1.5 pt-2.5 text-[10px] font-medium uppercase tracking-wider text-text-muted">
                Repositories
              </div>
              {repos.map((r) => (
                <button
                  key={r.slug}
                  type="button"
                  onClick={() => {
                    navigate(`/repos/${r.slug}`);
                    setOpen(false);
                  }}
                  className={cn(
                    'flex w-full cursor-pointer items-center gap-3 px-4 py-2 text-left transition-colors',
                    r.slug === repo
                      ? 'border-l-2 border-accent bg-accent/10 text-text-primary'
                      : 'text-text-secondary hover:bg-hover hover:text-text-primary',
                  )}
                >
                  <FolderOpen className="h-3.5 w-3.5 shrink-0 text-node-folder" />
                  <span className="flex-1 truncate font-mono text-sm">{r.displayName}</span>
                  {r.slug === repo && (
                    <span className="shrink-0 font-mono text-[10px] text-accent">active</span>
                  )}
                </button>
              ))}
            </div>
          )}
          <div className="border-t border-border-subtle">
            <button
              type="button"
              onClick={() => {
                navigate('/repos');
                setOpen(false);
              }}
              className="flex w-full cursor-pointer items-center gap-3 px-4 py-3 text-left text-sm text-text-secondary transition-colors hover:bg-hover hover:text-text-primary"
            >
              <Sparkles className="h-3.5 w-3.5 shrink-0 text-accent" />
              Manage repositories…
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
